import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { strings } from "@/i18n/strings";
import { useLang } from "@/context/LangContext";
import { useSite } from "@/context/SiteContext";
import { uploadFile } from "@/components/admin/SermonForm";
import { humanize } from "@/lib/design";
import { blockField, layoutOf } from "@/lib/blocks";

const norm = (s = "") => s.replace(/\s+/g, " ").trim();
const tell = (msg) => window.parent !== window && window.parent.postMessage(msg, window.location.origin);

// Finds which website-text key an element shows, by matching its visible text.
const useTextIndex = () => {
  const { t, lang } = useLang();
  const { settings } = useSite();
  return useMemo(() => {
    const keys = new Set([...Object.keys(strings.en), ...Object.keys(settings?.text?.en || {}), ...Object.keys(settings?.text?.mh || {})]);
    const map = new Map();
    keys.forEach((k) => {
      const v = norm(t(k));
      if (v.length > 1 && !map.has(v)) map.set(v, k);
    });
    return map;
  }, [t, lang, settings?.text]); // eslint-disable-line react-hooks/exhaustive-deps
};

const findText = (index, start) => {
  let el = start;
  for (let i = 0; el && i < 6 && el !== document.body; i += 1, el = el.parentElement) {
    if (el.closest("[data-ve-ui]")) return null;
    const key = index.get(norm(el.innerText));
    if (key) return { el, key };
  }
  return null;
};

const REC_LABEL = { sermons: "message", announcements: "announcement", gallery: "photo caption", schedule: "radio schedule", departments: "department name", pastors: "pastor" };
const SETTINGS_RECS = ["schedule", "departments"];
const recField = (rec, lang) => { const f = rec.split("|")[2]; if (f === "@") return lang; return f.endsWith("_") ? `${f}${lang}` : f; };
const findRec = (coll, list, id) => (coll === "schedule" ? list[Number(id)] : list.find((x) => (coll === "departments" ? x.slug : x.id) === id));

const Popover = ({ target, lang, onClose, onSave, zoom = 1 }) => {
  const [v, setV] = useState(target.value || "");
  const [busy, setBusy] = useState(false);
  const r = target.el.getBoundingClientRect();
  const top = Math.min(r.bottom + 8, window.innerHeight - 220 * zoom) / zoom;
  const left = Math.max(8, Math.min(r.left, window.innerWidth - 368 * zoom)) / zoom;
  const save = async () => { setBusy(true); await onSave(v); setBusy(false); };
  return (
    <div data-ve-ui data-testid="ve-text-popover" style={{ top, left }} className="fixed z-[9999] w-[360px] max-w-[calc(100vw-16px)] rounded-2xl border border-[#C8862F]/40 bg-white p-3 text-[#14213D] shadow-2xl">
      <p className="mb-2 font-ui text-[11px] uppercase tracking-widest text-[#8A6A12]">Edit {target.rec ? REC_LABEL[target.rec.split("|")[0]] : target.blk ? "block" : "text"}{target.rec && !target.rec.endsWith("_") && !target.rec.endsWith("@") ? "" : ` · ${lang === "mh" ? "Marshallese" : "English"}`}</p>
      {target.rec && <p className="mb-2 text-[11px] text-[#5F6875]">This saves straight to the website (no Publish needed).</p>}
      <textarea data-testid="ve-text-input" autoFocus rows={Math.min(8, Math.max(2, Math.ceil(v.length / 40)))} value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); } if (e.key === "Escape") onClose(); }}
        className="w-full resize-y rounded-xl border border-[#14213D]/15 bg-[#FBF8F2] p-2.5 font-ui text-sm outline-none focus:border-[#C8862F]" />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[11px] text-[#5F6875]">Enter = save · Shift+Enter = new line</span>
        <div className="flex gap-1.5">
          <button type="button" data-testid="ve-text-cancel" onClick={onClose} className="rounded-full p-2 text-[#5F6875] hover:bg-[#14213D]/5" aria-label="Cancel"><X className="h-4 w-4" /></button>
          <button type="button" data-testid="ve-text-save" onClick={save} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-[#14213D] px-4 py-2 font-ui text-xs font-semibold text-white">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Small toolbar for Select mode: hide or replace the block, text or photo that was clicked.
const SelectBar = ({ sel, onBigger, onAction, onClose, zoom = 1 }) => {
  const r = sel.block.getBoundingClientRect();
  const top = Math.max(8, Math.min(r.top + 8, window.innerHeight - 200 * zoom)) / zoom;
  const left = Math.max(8, Math.min(r.left + 8, window.innerWidth - 328 * zoom)) / zoom;
  const b = "rounded-full px-3 py-1.5 font-ui text-xs font-semibold transition-colors";
  return (
    <div data-ve-ui data-testid="ve-select-bar" style={{ top, left }} className="fixed z-[9999] w-[320px] max-w-[calc(100vw-16px)] rounded-2xl border border-[#2563EB]/40 bg-white p-3 text-[#14213D] shadow-2xl">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="truncate font-ui text-[11px] uppercase tracking-widest text-[#2563EB]">Selected: {humanize(sel.block.dataset.testid)}</p>
        <button type="button" data-testid="ve-select-close" onClick={onClose} className="rounded-full p-1 text-[#5F6875] hover:bg-[#14213D]/5" aria-label="Close"><X className="h-3.5 w-3.5" /></button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <button type="button" data-testid="ve-select-hide-block" onClick={() => onAction("hide-block")} className={`${b} bg-[#B4442A] text-white hover:bg-[#9A3A23]`}>Remove this block</button>
        {sel.text && <button type="button" data-testid="ve-select-replace-text" onClick={() => onAction("replace-text")} className={`${b} bg-[#14213D] text-white`}>Replace text</button>}
        {sel.text && <button type="button" data-testid="ve-select-hide-text" onClick={() => onAction("hide-text")} className={`${b} border border-[#B4442A]/40 text-[#B4442A]`}>Remove text only</button>}
        {sel.img && <button type="button" data-testid="ve-select-replace-photo" onClick={() => onAction("replace-photo")} className={`${b} bg-[#14213D] text-white`}>Replace photo</button>}
        {sel.img && <button type="button" data-testid="ve-select-hide-photo" onClick={() => onAction("hide-photo")} className={`${b} border border-[#B4442A]/40 text-[#B4442A]`}>Remove photo only</button>}
        <button type="button" data-testid="ve-select-bigger" onClick={onBigger} className={`${b} border border-[#2563EB]/40 text-[#2563EB]`}>Select bigger area</button>
      </div>
      <p className="mt-2 text-[11px] text-[#5F6875]">Removed items can be brought back in Sections → Removed items.</p>
    </div>
  );
};

const blockOf = (el) => {
  const b = el?.closest?.("[data-testid]");
  return b && !b.closest("[data-ve-ui]") && b !== document.body ? b : null;
};

// Runs inside the portal's preview frame: click text to edit it, click a photo to replace it.
export const EditOverlay = () => {
  const { veMode, setDraft, settings } = useSite();
  const { t, lang, setLang } = useLang();
  const { pathname } = useLocation();
  const index = useTextIndex();
  const [target, setTarget] = useState(null);
  const [mode, setMode] = useState("text");
  const [uiZoom, setUiZoom] = useState(1);
  const [sel, setSel] = useState(null);
  const fileRef = useRef(null);
  const imgKey = useRef("");
  const hover = useRef(null);
  const editing = veMode === "edit" && window.parent !== window;
  const tRef = useRef(t);
  tRef.current = t;
  const langRef = useRef(lang);
  langRef.current = lang;

  const openRec = useCallback(async (el) => {
    const rec = el.dataset.veRec;
    const [coll, id] = rec.split("|");
    try {
      const { data } = await api.get(SETTINGS_RECS.includes(coll) ? "/settings" : `/${coll}`);
      const list = SETTINGS_RECS.includes(coll) ? data?.[coll] : data;
      const record = findRec(coll, Array.isArray(list) ? list : [], id);
      if (record) setTarget({ el, rec, record, value: record[recField(rec, langRef.current)] || "" });
    } catch { toast.error("Could not load this item"); }
  }, []);

  const layoutsRef = useRef({});
  layoutsRef.current = settings?.layouts || {};
  // Added blocks (headings, text, announcements, photos, videos): text opens the editor, photos/videos open the file picker.
  const openBlk = useCallback((el) => {
    const blk = el.dataset.veBlk;
    const [page, id, f] = blk.split("|");
    const block = layoutOf(page, layoutsRef.current[page]).find((b) => b.id === id);
    if (!block) return;
    if (f === "url") {
      imgKey.current = `blk:${blk}`;
      if (fileRef.current) fileRef.current.accept = block.type === "video" ? "video/*" : "image/*";
      fileRef.current?.click();
      return;
    }
    setTarget({ el, blk, value: block[blockField(f, langRef.current)] || "" });
  }, []);

  const saved = useCallback((data, undo) => { setDraft(data.data); tell({ type: "ve-saved", changes: data.changes, undo }); }, [setDraft]);

  useEffect(() => { if (editing) tell({ type: "ve-path", path: pathname }); }, [editing, pathname]);

  useEffect(() => {
    if (!editing) return undefined;
    document.documentElement.classList.add("ve-editing");
    document.documentElement.classList.toggle("ve-selecting", mode === "select");
    const onClick = (e) => {
      if (e.target.closest("[data-ve-ui]")) return;
      if (mode === "select") {
        e.preventDefault();
        e.stopPropagation();
        const block = blockOf(e.target);
        if (!block) return;
        const img = document.elementsFromPoint(e.clientX, e.clientY).find((n) => n.dataset?.veImg);
        setTarget(null);
        setSel({ block, text: findText(index, e.target), img: img?.dataset.veImg || "" });
        return;
      }
      const blkEl = e.target.closest("[data-ve-blk]");
      if (blkEl) {
        e.preventDefault();
        e.stopPropagation();
        openBlk(blkEl);
        return;
      }
      const recEl = e.target.closest("[data-ve-rec]");
      if (recEl) {
        e.preventDefault();
        e.stopPropagation();
        openRec(recEl);
        return;
      }
      const hit = findText(index, e.target);
      if (hit) {
        e.preventDefault();
        e.stopPropagation();
        setTarget({ ...hit, value: tRef.current(hit.key) });
        return;
      }
      const img = document.elementsFromPoint(e.clientX, e.clientY).find((n) => n.dataset?.veImg);
      if (img) {
        e.preventDefault();
        e.stopPropagation();
        imgKey.current = img.dataset.veImg;
        if (fileRef.current) fileRef.current.accept = "image/*";
        fileRef.current?.click();
      }
    };
    const onOver = (e) => {
      hover.current?.classList.remove("ve-hover", "ve-select-hover");
      if (mode === "select") {
        hover.current = blockOf(e.target);
        hover.current?.classList.add("ve-select-hover");
        return;
      }
      hover.current = e.target.closest("[data-ve-blk]") || e.target.closest("[data-ve-rec]") || findText(index, e.target)?.el || null;
      hover.current?.classList.add("ve-hover");
    };
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "ve-lang") setLang(e.data.lang);
      if (e.data?.type === "ve-mode") { setMode(e.data.mode); setSel(null); }
      if (e.data?.type === "ve-scale" && e.data.scale > 0) setUiZoom(Math.min(1.6, 1 / e.data.scale));
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("mouseover", onOver);
    window.addEventListener("message", onMsg);
    return () => {
      document.documentElement.classList.remove("ve-editing", "ve-selecting");
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("mouseover", onOver);
      window.removeEventListener("message", onMsg);
    };
  }, [editing, index, setLang, mode, openBlk, openRec]);

  useEffect(() => {
    if (!sel) return undefined;
    sel.block.classList.add("ve-selected");
    return () => sel.block.classList.remove("ve-selected");
  }, [sel]);

  if (veMode === "preview") {
    return (
      <div data-ve-ui data-testid="ve-preview-bar" className="fixed inset-x-0 bottom-0 z-[9999] flex flex-wrap items-center justify-center gap-3 bg-[#14213D] px-4 py-2.5 font-ui text-sm text-white">
        Draft preview: visitors don&apos;t see these changes until you press Publish.
        <a href="/?ve=off" data-testid="ve-preview-exit" className="rounded-full bg-white px-4 py-1 text-xs font-semibold text-[#14213D]">Exit preview</a>
      </div>
    );
  }
  if (!editing) return null;

  const saveBlock = async (blk, value) => {
    const [page, id, f] = blk.split("|");
    const prev = settings?.layouts?.[page];
    const list = layoutOf(page, prev).map((b) => (b.id === id ? { ...b, [blockField(f, lang)]: value } : b));
    const { data } = await api.put("/design/draft", { layouts: { [page]: list } });
    saved(data, { layouts: { [page]: prev ?? "" } });
  };
  const saveText = async (value) => {
    if (target.blk) {
      try { await saveBlock(target.blk, value); setTarget(null); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      return;
    }
    if (target.rec) {
      const [coll, id] = target.rec.split("|");
      const changed = { ...target.record, [recField(target.rec, lang)]: value };
      try {
        let data;
        if (SETTINGS_RECS.includes(coll)) {
          const live = (await api.get("/settings")).data?.[coll] || [];
          const list = live.map((x, i) => ((coll === "schedule" ? String(i) : x.slug) === id ? changed : x));
          ({ data } = await api.put("/settings", { [coll]: list }));
        } else {
          ({ data } = await api.put(`/${coll}/${id}`, changed));
        }
        setTarget(null);
        if (data?.pending) toast("Sent for approval. It will show once a Super Admin approves it.");
        else window.location.reload();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      return;
    }
    const prev = settings?.text?.[lang]?.[target.key] ?? "";
    const { data } = await api.put("/design/draft", { text: { [lang]: { [target.key]: value } } });
    saved(data, { text: { [lang]: { [target.key]: prev } } });
    setTarget(null);
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    tell({ type: "ve-busy", busy: true });
    try {
      const url = await uploadFile(f);
      if (imgKey.current.startsWith("blk:")) {
        await saveBlock(imgKey.current.slice(4), url);
        return;
      }
      const prev = settings?.images?.[imgKey.current] ?? "";
      const { data } = await api.put("/design/draft", { images: { [imgKey.current]: url } });
      saved(data, { images: { [imgKey.current]: prev } });
    } finally {
      tell({ type: "ve-busy", busy: false });
    }
  };
  const hide = async (key) => {
    const { data } = await api.put("/design/draft", { hidden: { [key]: true } });
    saved(data, { hidden: { [key]: "" } });
    setSel(null);
  };
  const onAction = (a) => {
    if (a === "hide-block") return hide(`b:${sel.block.dataset.testid}`);
    if (a === "hide-text") return hide(`t:${sel.text.key}`);
    if (a === "hide-photo") return hide(`img:${sel.img}`);
    if (a === "replace-text") { setTarget({ ...sel.text, value: t(sel.text.key) }); setSel(null); return null; }
    imgKey.current = sel.img;
    setSel(null);
    if (fileRef.current) fileRef.current.accept = "image/*";
    return fileRef.current?.click();
  };
  const bigger = () => {
    const up = blockOf(sel.block.parentElement);
    if (up && up.dataset.testid !== "home-page") setSel({ ...sel, block: up });
  };
  return (
    <>
      <input ref={fileRef} data-ve-ui type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="ve-image-input" />
      {sel && <div style={{ zoom: uiZoom }}><SelectBar sel={sel} zoom={uiZoom} onBigger={bigger} onAction={onAction} onClose={() => setSel(null)} /></div>}
      {target && <div style={{ zoom: uiZoom }}><Popover key={target.key || target.rec || target.blk} target={target} zoom={uiZoom} lang={lang} onClose={() => setTarget(null)} onSave={saveText} /></div>}
    </>
  );
};
